import { useCallback } from "react";
import { Link } from "react-router-dom";
import { routes } from "@/app/navigation/routes";
import { heroImage } from "@/assets/images";
import { AddressSearch, AddressSuggestion } from "@/shared/components/common/AddressSearch";
import { propertySupportService } from "@/features/properties/model/propertyServices";
import styles from "./heroSection.module.css";
import { useAuthViewModel } from "@/features/auth/viewmodel/useAuthViewModel";
import { tokenService } from "@/services/auth/token.service";
/**
 * HeroSection Component
 * Main hero section with address input and CTA
 */
export const HeroSection = () => {
  const searchAddresses = useCallback(async (query: string): Promise<AddressSuggestion[]> => {
    if (!query || query.trim().length < 3) {
      return [];
    }

    try {
      const results = await propertySupportService.getRealStateAutoCompletePropertyDetails({
        search: query.trim(),
        search_types: ["A"],
      });

      console.log("API results:", results);

      if (!results || !Array.isArray(results) || results.length === 0) {
        console.log("No results returned from API");
        return [];
      }

      const mappedResults = results
        .map((item): AddressSuggestion | null => {
          // Build address string from available fields
          const addressParts: string[] = [];

          if (item.house) addressParts.push(item.house.trim());
          if (item.street) addressParts.push(item.street.trim());

          const streetAddress = addressParts.join(" ").trim();

          // Use provided address or build from parts
          let fullAddress = item.address || "";

          if (!fullAddress && streetAddress) {
            const cityStateZip = [item.city || "", item.state || "", item.zip || ""]
              .filter(Boolean)
              .join(", ");

            fullAddress = cityStateZip ? `${streetAddress}, ${cityStateZip}`.trim() : streetAddress;
          } else if (!fullAddress) {
            // Fallback: use city, state, zip if no street address
            const parts = [item.city || "", item.state || "", item.zip || ""].filter(Boolean);
            fullAddress = parts.join(", ").trim();
          }

          // Ensure we have at least some address data
          if (!fullAddress && !item.id) {
            return null;
          }

          return {
            id: item.id || `address-${Date.now()}-${Math.random()}`,
            address: fullAddress || "Address not available",
            city: item.city,
            state: item.state,
            zipCode: item.zip,
            apn: item.apn,
          };
        })
        .filter((item): item is AddressSuggestion => item !== null);

      console.log("Mapped suggestions:", mappedResults);
      return mappedResults;
    } catch (error) {
      console.error("Error searching addresses from hero:", error);
      return [];
    }
  }, []);

  const { isAuthenticated } = useAuthViewModel();
  const hasToken = tokenService.hasToken();

  return (
    <section
      id="home"
      className={styles.heroSection}
      style={{
        backgroundImage: `linear-gradient(
          to bottom,
          rgba(59, 130, 246, 0.35) 0%,
          rgba(37, 99, 235, 0.25) 50%,
          rgba(30, 64, 175, 0.3) 100%
        ), url(${heroImage})`,
      }}
    >
      <div className={styles.heroContent}>
        <h1 className={styles.heroTitle}>
          Effortlessly Protest
          <br />
          Your <span className={styles.texasHighlight}>Texas</span>
          <br />
          Property Taxes
        </h1>

        <div className={styles.searchContainer}>
          <AddressSearch
            placeholder="Enter address"
            buttonText="Get Estimate Savings"
            searchAddresses={searchAddresses}
            onAddressSelect={(address) => {
              console.log("Address selected:", address);
            }}
            onButtonClick={(address) => {
              console.log("Button clicked with address:", address);
            }}
          />
        </div>

        {!isAuthenticated && !hasToken && (
          <div className={styles.authLinks}>
            <Link to={routes.signup} className={styles.authLink}>
              Get Started
            </Link>
            <Link to={routes.login} className={styles.authLink}>
              Sign In
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};
